"use client";

import { useState, useEffect, useCallback } from "react";
import { useGlobalFilters } from "@/contexts/GlobalFilterContext";
import { MapCompany } from "@/types/company";
import { fetchCompanyMetaData, fetchCompaniesByBoundingBox } from "@/lib/api/companies";
import { debounce } from "@/lib/utils";
import { CompanyMetadataResponseDto } from "@/lib/api/apiwrapper";

export function useCompaniesMap() {
  const [companies, setCompanies] = useState<MapCompany[]>([]);
  const [metaData, setMetaData] = useState<CompanyMetadataResponseDto>({});
  const [didFetchCompanies, setDidFetchCompanies] = useState(false);
  const { globalFilters } = useGlobalFilters();
  const [categories, setCategories] = useState<string[]>([]);
  const [orderAmountRange, setOrderAmountRange] = useState<{ min?: number; max?: number }>({});
  const [boundingBox, setBoundingBox] = useState<number[][]>([
    [4.345, 51.998],
    [4.369, 52.025],
  ]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchCompanyMetaData(globalFilters.year)
      .then(setMetaData)
      .catch((error) => console.error("Error fetching metadata:", error));
  }, [globalFilters.year]);

  const fetchCompanies = useCallback(
    async (bbox: number[][]) => {
      setIsLoading(true);
      try {
        const data = await fetchCompaniesByBoundingBox(bbox, {
          categories,
          totalOrderAmount: orderAmountRange,
          year: globalFilters.year,
        });
        setCompanies(data);
        setDidFetchCompanies(true);
      } catch (error) {
        console.error("Error fetching companies:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [categories, orderAmountRange, globalFilters.year],
  );

  const debouncedFetchCompanies = useCallback(
    debounce((bbox: number[][]) => fetchCompanies(bbox), 250),
    [fetchCompanies],
  );

  useEffect(() => {
    if (boundingBox.length === 2) {
      debouncedFetchCompanies(boundingBox);
    }
  }, [boundingBox, debouncedFetchCompanies]);

  const handleBoundingBoxChange = useCallback((bbox: number[][]) => {
    setBoundingBox(bbox);
  }, []);

  return {
    companies,
    metaData,
    isLoading,
    didFetchCompanies,
    handleBoundingBoxChange,
    categories,
    setCategories,
    orderAmountRange,
    setOrderAmountRange,
  };
}
